import { Fragment, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { Kpi } from '@/components/brand/Kpi';
import { ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';
import { useApiQuery } from '@/lib/hooks';
import { api, ApiError, type WebApp, type WebAppsManifest } from '@/lib/api';

const CATEGORIES: { key: string; label: string; blurb: string }[] = [
  { key: 'App', label: 'Apps', blurb: 'User-facing web apps' },
  { key: 'Service', label: 'Services & APIs', blurb: 'Headless services and document/API workers' },
  { key: 'Tool', label: 'Tools', blurb: 'Internal tooling, MCP servers and CLIs' },
  { key: 'Docs', label: 'Docs', blurb: 'Reference & guide repositories' },
  { key: 'Other', label: 'Other', blurb: 'Uncategorised repositories' },
];

// Fixed column widths so every row across every category lines up.
const COL_WIDTHS = ['20%', '17%', '9%', '12%', '12%', '17%', '13%'];

function Cols() {
  return (
    <colgroup>
      {COL_WIDTHS.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
}

function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const sec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

function fullDate(iso: string | null | undefined): string {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return d.toLocaleString('en-GB', { timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short' });
}

function Dot({ on, title }: { on: boolean; title?: string }) {
  return (
    <span
      title={title}
      aria-label={on ? 'yes' : 'no'}
      className="inline-block w-2 h-2 rounded-full align-middle"
      style={{ background: on ? 'var(--sev-ok)' : 'transparent', border: on ? 'none' : '1px solid var(--rule)' }}
    />
  );
}

function DocChip({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className="inline-block px-1.5 py-0.5 text-caption mono rounded-sm"
      style={{
        color: on ? 'var(--fg-default)' : 'var(--fg-muted)',
        background: on ? 'var(--bg-section)' : 'transparent',
        border: '1px solid var(--rule)',
        opacity: on ? 1 : 0.5,
      }}
      title={on ? `${label} present` : `no ${label}`}
    >
      {label}
    </span>
  );
}

function Row({ app }: { app: WebApp }) {
  return (
    <tr className="align-top hover:bg-bg-section/30">
      {/* Project + description */}
      <td className="py-3 pr-3">
        <div className="flex items-center gap-2">
          {app.url ? (
            <a href={app.url} target="_blank" rel="noreferrer" className="text-fg-default font-bold hover:underline">
              {app.title}
            </a>
          ) : (
            <span className="text-fg-default font-bold">{app.title}</span>
          )}
        </div>
        <div className="text-caption text-fg-muted mono mt-0.5 break-all">{app.name}</div>
        {app.description && <div className="text-caption text-fg-muted mt-1">{app.description}</div>}
      </td>

      {/* Live + staging URLs */}
      <td className="py-3 px-3">
        {app.url ? (
          <a href={app.url} target="_blank" rel="noreferrer" className="text-small hover:underline mono break-all" style={{ color: 'var(--sev-ok)' }}>
            {app.url.replace(/^https?:\/\//, '')}
          </a>
        ) : (
          <span className="text-fg-muted text-small">—</span>
        )}
        {app.urlStaging && (
          <a
            href={app.urlStaging}
            target="_blank"
            rel="noreferrer"
            className="block text-caption text-fg-muted hover:underline mono break-all mt-1"
            title="Staging"
          >
            staging: {app.urlStaging.replace(/^https?:\/\//, '')}
          </a>
        )}
      </td>

      {/* Version */}
      <td className="py-3 px-3 num text-small">
        {app.version ? (
          <span title={app.versionSource ? `from ${app.versionSource}` : undefined}>v{app.version}</span>
        ) : (
          <span className="text-fg-muted">—</span>
        )}
      </td>

      {/* Git */}
      <td className="py-3 px-3 text-small">
        <div className="flex items-start gap-2">
          <span className="mt-1.5 shrink-0">
            <Dot on={app.git.present} title={app.git.present ? 'In GitHub' : 'No GitHub repo'} />
          </span>
          {app.git.present ? (
            <span title={`pushed ${fullDate(app.git.lastPushed)}`}>
              {app.repoUrl ? (
                <a href={app.repoUrl} target="_blank" rel="noreferrer" className="hover:underline text-fg-default">
                  {app.git.language || 'repo'}
                </a>
              ) : (
                app.git.language || 'repo'
              )}
              <span className="text-fg-muted"> · {ago(app.git.lastPushed)}</span>
            </span>
          ) : (
            <span className="text-fg-muted">none</span>
          )}
        </div>
      </td>

      {/* Cloudflare */}
      <td className="py-3 px-3 text-small">
        <div className="flex items-start gap-2">
          <span className="mt-1.5 shrink-0">
            <Dot on={app.cloudflare.present} title={app.cloudflare.present ? 'Deployed on Cloudflare' : 'Not on Cloudflare'} />
          </span>
          {app.cloudflare.present ? (
            <span title={`last deploy ${fullDate(app.cloudflare.lastModified)}`}>
              {app.cloudflare.resourceCount} res
              <span className="text-fg-muted"> · {ago(app.cloudflare.lastModified)}</span>
            </span>
          ) : (
            <span className="text-fg-muted">none</span>
          )}
        </div>
      </td>

      {/* Docs */}
      <td className="py-3 px-3">
        <div className="flex flex-wrap gap-1">
          <DocChip on={app.docs.readme} label="README" />
          <DocChip on={app.docs.claude} label="CLAUDE" />
          <DocChip on={app.docs.changelog} label="CHANGELOG" />
        </div>
      </td>

      {/* Last edited (last commit on default branch) */}
      <td className="py-3 pl-3 text-small">
        {app.git.lastCommit ? (
          <span title={fullDate(app.git.lastCommit)}>
            {ago(app.git.lastCommit)}
            <span className="text-fg-muted"> · {app.git.lastAuthor ?? 'unknown'}</span>
          </span>
        ) : (
          <span className="text-fg-muted">—</span>
        )}
      </td>
    </tr>
  );
}

export function WebAppsPage() {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);

  const q = useApiQuery<WebAppsManifest>({
    queryKey: ['web-apps'],
    queryFn: () => api.webApps(),
    staleTime: 5 * 60_000,
  });

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const fresh = await api.webApps({ refresh: true });
      queryClient.setQueryData(['web-apps'], fresh);
    } catch {
      // surfaced by the query on next render; keep the button responsive
      await queryClient.invalidateQueries({ queryKey: ['web-apps'] });
    } finally {
      setRefreshing(false);
    }
  }

  const data = q.data;
  const present = data ? CATEGORIES.filter((c) => data.apps.some((a) => a.category === c.key)) : [];

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Directory"
        title="Web Apps"
        meta={
          <div className="flex items-center gap-4">
            {data && (
              <span>
                {data.count} projects · refreshed {ago(data.generatedAt)}
              </span>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-eyebrow uppercase tracking-heading border border-rule px-3 py-1.5 hover:bg-bg-section disabled:opacity-50"
              title="Rebuild live from GitHub + Cloudflare"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        }
      />

      {q.isLoading && <LoadingState label="Loading directory" />}
      {q.error instanceof ApiError && q.error.isUnauthorized() && <UnauthorizedState />}
      {q.error && !(q.error instanceof ApiError && q.error.isUnauthorized()) && (
        <ErrorState title="Couldn't load the directory" detail={(q.error as Error).message} />
      )}

      {data && (
        <>
          {/* At-a-glance stats. */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <Kpi label="Projects" value={data.stats.total} detail={`${data.stats.apps} user-facing apps`} />
            <Kpi label="Live" value={data.stats.live} detail="reachable on a custom domain" />
            <Kpi label="Deployed · 7 days" value={data.stats.deployedThisWeek} detail="Cloudflare deploys this week" />
            <Kpi
              label="Stale · >90 days"
              value={data.stats.stale90}
              detail="no Cloudflare deploy in 90 days"
              variant={data.stats.stale90 > 0 ? 'warn' : 'ok'}
            />
          </div>

          <Panel title="Directory" subtitle={`${data.count} projects`}>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px] table-fixed text-small border-collapse">
                <Cols />
                <thead className="text-fg-muted text-eyebrow">
                  <tr className="border-b border-rule">
                    <th className="text-left font-bold py-2 pr-3">Project</th>
                    <th className="text-left font-bold py-2 px-3">Live · staging</th>
                    <th className="text-left font-bold py-2 px-3">Version</th>
                    <th className="text-left font-bold py-2 px-3">Git</th>
                    <th className="text-left font-bold py-2 px-3">Cloudflare</th>
                    <th className="text-left font-bold py-2 px-3">Docs</th>
                    <th className="text-left font-bold py-2 pl-3">Last edited</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule">
                  {present.map((cat) => {
                    const rows = data.apps.filter((a) => a.category === cat.key);
                    return (
                      <Fragment key={cat.key}>
                        <tr className="bg-bg-section/40">
                          <td colSpan={7} className="py-2 px-1">
                            <span className="text-eyebrow font-bold uppercase tracking-heading text-fg-default">
                              {cat.label}
                            </span>
                            <span className="text-caption text-fg-muted">
                              {' '}· {rows.length} · {cat.blurb}
                            </span>
                          </td>
                        </tr>
                        {rows.map((a) => (
                          <Row key={a.name} app={a} />
                        ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Panel>

          <p className="text-caption text-fg-muted">
            Live from the GitHub &amp; Cloudflare APIs, cached server-side. Versions read from each repo&apos;s
            CHANGELOG (falling back to package.json). Auto-refreshes daily; <span className="mono">Refresh</span>{' '}
            rebuilds on demand.
          </p>
        </>
      )}
    </div>
  );
}
